import maplibregl from 'maplibre-gl';
import { PMTiles, Protocol } from 'pmtiles';

import type { MapBasemapTheme } from '@/context/PreferencesContext';
import { prepareOverlayStyleForComposition } from '@/lib/overlayStyle';

const SYSTEM_BASEMAP_SOURCE_ID = 'system-basemap';
const SYSTEM_BASEMAP_URL = '/api/v1/system/basemaps/osm.pmtiles';
const SYSTEM_BASEMAP_STYLE_URL = (theme: MapBasemapTheme) => `/api/v1/system/basemaps/styles/${encodeURIComponent(theme)}`;

let pmtilesProtocol: Protocol | null = null;
let pmtilesProtocolRegistered = false;
const pmtilesRegistry = new Map<string, PMTiles>();
const basemapStyleCache = new Map<MapBasemapTheme, Promise<Record<string, unknown>>>();

const cloneJson = <T,>(value: T): T => {
  if (typeof structuredClone === 'function') {
    return structuredClone(value);
  }
  return JSON.parse(JSON.stringify(value)) as T;
};

const toAbsoluteUrl = (url: string): string => {
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  if (typeof window === 'undefined') {
    return url;
  }
  return new URL(url, window.location.origin).toString().replace(/%7B/gi, '{').replace(/%7D/gi, '}');
};

const ensurePMTilesProtocol = (): Protocol => {
  if (!pmtilesProtocol) {
    pmtilesProtocol = new Protocol();
  }
  if (!pmtilesProtocolRegistered) {
    maplibregl.addProtocol('pmtiles', pmtilesProtocol.tile);
    pmtilesProtocolRegistered = true;
  }
  return pmtilesProtocol;
};

export const ensurePMTilesArchiveProtocol = (archiveUrl: string) => {
  const protocol = ensurePMTilesProtocol();
  const absoluteUrl = toAbsoluteUrl(archiveUrl);
  if (!pmtilesRegistry.has(absoluteUrl)) {
    const instance = new PMTiles(absoluteUrl);
    pmtilesRegistry.set(absoluteUrl, instance);
    protocol.add(instance);
  }
  return `pmtiles://${absoluteUrl}`;
};

export const ensureSystemBasemapProtocol = () => {
  return ensurePMTilesArchiveProtocol(SYSTEM_BASEMAP_URL);
};

const fetchSystemBasemapStyle = async (theme: MapBasemapTheme) => {
  const response = await fetch(SYSTEM_BASEMAP_STYLE_URL(theme), {
    credentials: 'include',
    cache: 'no-store',
  });
  if (!response.ok) {
    throw new Error(`Failed to load ${theme} basemap (${response.status})`);
  }
  return (await response.json()) as Record<string, unknown>;
};

const loadSystemBasemapStyle = async (theme: MapBasemapTheme) => {
  if (!basemapStyleCache.has(theme)) {
    basemapStyleCache.set(
      theme,
      fetchSystemBasemapStyle(theme).catch(error => {
        basemapStyleCache.delete(theme);
        throw error;
      })
    );
  }
  return cloneJson(await basemapStyleCache.get(theme)!);
};

export const stripSystemBasemapArtifacts = (style: Record<string, unknown>) => {
  const nextStyle = cloneJson(style);
  const styleSources =
    nextStyle.sources && typeof nextStyle.sources === 'object'
      ? { ...(nextStyle.sources as Record<string, unknown>) }
      : {};
  delete styleSources[SYSTEM_BASEMAP_SOURCE_ID];
  nextStyle.sources = styleSources;

  const layers = Array.isArray(nextStyle.layers) ? (nextStyle.layers as Array<Record<string, unknown>>) : [];
  nextStyle.layers = layers.filter(layer => {
    const source = typeof layer.source === 'string' ? layer.source : '';
    const metadata = layer.metadata && typeof layer.metadata === 'object' ? (layer.metadata as Record<string, unknown>) : null;
    return source !== SYSTEM_BASEMAP_SOURCE_ID && metadata?.system_basemap !== true;
  });

  const glyphs = typeof nextStyle.glyphs === 'string' ? nextStyle.glyphs : '';
  if (glyphs.includes('/api/v1/system/basemaps/fonts/')) {
    delete nextStyle.glyphs;
  }

  return nextStyle;
};

const normalizeBasemapStyle = async (theme: MapBasemapTheme) => {
  const style = await loadSystemBasemapStyle(theme);
  const nextStyle = cloneJson(style);
  const styleSources =
    nextStyle.sources && typeof nextStyle.sources === 'object'
      ? { ...(nextStyle.sources as Record<string, unknown>) }
      : {};

  if (styleSources[SYSTEM_BASEMAP_SOURCE_ID] && typeof styleSources[SYSTEM_BASEMAP_SOURCE_ID] === 'object') {
    styleSources[SYSTEM_BASEMAP_SOURCE_ID] = {
      ...(styleSources[SYSTEM_BASEMAP_SOURCE_ID] as Record<string, unknown>),
      url: ensureSystemBasemapProtocol(),
    };
  }

  nextStyle.sources = styleSources;
  if (typeof nextStyle.glyphs === 'string') {
    nextStyle.glyphs = toAbsoluteUrl(nextStyle.glyphs);
  }
  return nextStyle;
};

export const composeStyleWithSystemBasemap = async (
  overlayStyle: Record<string, unknown>,
  basemapTheme: MapBasemapTheme
) => {
  const basemapStyle = await normalizeBasemapStyle(basemapTheme);
  const nextStyle = cloneJson(basemapStyle);
  const preparedOverlay = prepareOverlayStyleForComposition({
    basemapLayerIds: (((basemapStyle.layers as Array<Record<string, unknown>> | undefined) ?? [])
      .map(layer => (typeof layer.id === 'string' ? layer.id : ''))
      .filter(Boolean)),
    basemapSourceIds: Object.keys((basemapStyle.sources as Record<string, unknown> | undefined) ?? {}),
    overlayStyle: stripSystemBasemapArtifacts(overlayStyle),
  });
  const overlaySources =
    preparedOverlay.sources && typeof preparedOverlay.sources === 'object'
      ? (preparedOverlay.sources as Record<string, unknown>)
      : {};
  const overlayLayers = Array.isArray(preparedOverlay.layers)
    ? (preparedOverlay.layers as Array<Record<string, unknown>>)
    : [];

  nextStyle.version = typeof preparedOverlay.version === 'number' ? preparedOverlay.version : 8;
  nextStyle.name =
    typeof preparedOverlay.name === 'string' && preparedOverlay.name.trim()
      ? preparedOverlay.name
      : (nextStyle.name as string | undefined) ?? 'Generated map style';
  if (preparedOverlay.state && typeof preparedOverlay.state === 'object' && !Array.isArray(preparedOverlay.state)) {
    nextStyle.state = cloneJson(preparedOverlay.state);
  } else {
    delete nextStyle.state;
  }
  const overlaySprite =
    typeof preparedOverlay.sprite === 'string' && preparedOverlay.sprite.trim()
      ? toAbsoluteUrl(preparedOverlay.sprite)
      : null;
  if (overlaySprite) {
    nextStyle.sprite = overlaySprite;
  } else {
    delete nextStyle.sprite;
  }
  const overlayGlyphs =
    typeof preparedOverlay.glyphs === 'string' && preparedOverlay.glyphs.trim()
      ? toAbsoluteUrl(preparedOverlay.glyphs)
      : null;
  if (overlayGlyphs) {
    nextStyle.glyphs = overlayGlyphs;
  }
  nextStyle.sources = {
    ...((nextStyle.sources as Record<string, unknown> | undefined) ?? {}),
    ...overlaySources,
  };
  nextStyle.layers = [...((nextStyle.layers as Array<Record<string, unknown>> | undefined) ?? []), ...overlayLayers];

  return nextStyle;
};
